import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  DSButton,
  DSCircularButton,
  DSInput,
} from "@components/design-system/molecules";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import EDIT_USER from "../../../graphql/mutations/EditUser";
import currentUserQuery from "../../../graphql/queries/CurrentUser";
import { useAccount } from "../../../hooks";
import Twemoji from "../../../Twemoji";
import { defaultErrorHandler } from "../../../utils/operationTracking";
import { alert } from "../../common/Alert";
import EditDeleteButtons from "../../common/buttons/EditDeleteButtons";

const CONFIRM_USER_MUTATION = gql`
  mutation confirmUser($confirmationCode: String) {
    confirmUser(confirmationCode: $confirmationCode) {
      ok
    }
  }
`;

export const RESEND_CONFIRMATION_EMAIL_MUTATION = gql`
  mutation resendConfirmUser {
    resendConfirmUser {
      ok
    }
  }
`;

export default function RegisterConfirm() {
  const [isEditingEmail, setIsEditingEmail] = useState(false);

  const {
    control,
    handleSubmit,
    resetField,
    getValues,
    formState: { errors },
  } = useForm();

  const navigate = useNavigate();

  const [confirmUser] = useMutation(CONFIRM_USER_MUTATION);
  const [resendConfirmUser] = useMutation(
    RESEND_CONFIRMATION_EMAIL_MUTATION,
  );
  const [editUser] = useMutation(EDIT_USER);

  const { data, refetch } = useQuery(currentUserQuery, {
    fetchPolicy: "no-cache",
  });

  const { refetchAccountDetails } = useAccount();

  function submitConfirmation({ confirmationCode }) {
    confirmUser({
      variables: {
        confirmationCode,
      },
    })
      .then((response) => {
        const { ok } = response.data.confirmUser;

        refetchAccountDetails();
        if (ok) {
          navigate("/account");
        }
      })
      .catch(defaultErrorHandler);
  }

  if (!data) {
    return null;
  }

  return (
    <form
      onSubmit={handleSubmit(submitConfirmation)}
      className="mt-16 flex flex-col items-center space-y-10"
    >
      <div className="flex flex-col md:flex-row items-center md:space-x-4">
        <span className="text-4xl transform animate-[handwave_1s_ease-in-out_infinite]">
          <Twemoji emoji="👋" />
        </span>{" "}
        <h2 className="text-center text-3xl font-extrabold text-primary">
          Hey {data?.user?.firstName ?? ""}! Please confirm your
          email.
        </h2>
      </div>

      <div className="flex flex-col w-full max-w-[1200px] lg:items-stretch lg:flex-row lg:items-start items-center justify-center space-y-10 lg:space-x-12 lg:space-y-0 pt-10">
        <div className="flex w-3/4 lg:w-1/2 flex-1 flex-col items-center space-y-8">
          <div className="flex flex-row items-center space-x-2">
            <div className="h-8 w-8 bg-brand text-white font-bold rounded-full flex items-center justify-center">
              1
            </div>
            <h4>Send confirmation email</h4>
          </div>

          <div className="w-full justify-center flex-1 flex flex-col space-y-4 p-10 rounded-xl shadow-md shadow-secondary bg-primary">
            <div className="flex flex-row items-center justify-center space-x-2">
              {isEditingEmail ? (
                <div className="flex flex-col items-center w-full">
                  <Controller
                    control={control}
                    render={({ field: { onChange } }) => (
                      <DSInput
                        disabled={!isEditingEmail}
                        onChange={(event) => {
                          onChange(event.target.value);
                          if (!isEditingEmail) {
                            if (event.target.value.length > 0) {
                              isEditingEmail(true);
                            }
                          }
                        }}
                        defaultValue={data?.user?.email}
                        type="email"
                        dataTestId="register-confirm-email"
                      />
                    )}
                    name="email"
                    rules={{
                      required: isEditingEmail,
                      pattern: {
                        value: /\S+@\S+\.\S+/,
                        message:
                          "Entered value does not match email format",
                      },
                    }}
                  />
                  {errors.email && (
                    <p className="text-danger text-xs text-center mt-1">
                      Please enter a valid email address!
                    </p>
                  )}
                </div>
              ) : (
                <h4 className="truncate">{data?.user?.email}</h4>
              )}

              {!isEditingEmail ? (
                <DSCircularButton
                  size="sm"
                  icon={faPen}
                  dataTestId="edit-email-button"
                  onClick={() => {
                    setIsEditingEmail(true);
                  }}
                />
              ) : (
                <EditDeleteButtons
                  onClickDestructiveButton={() => {
                    resetField("email");
                    setIsEditingEmail(false);
                  }}
                  destructiveButtonText="Cancel"
                  onClickActionButton={async () => {
                    const { email } = getValues();

                    if (email == data?.user?.email || !email) {
                      setIsEditingEmail(false);
                      return;
                    }

                    editUser({ variables: { email } })
                      .then(() => {
                        alert("success", `Email changed to ${email}`);
                        refetch();
                      })
                      .catch(defaultErrorHandler);

                    setIsEditingEmail(false);
                  }}
                  actionButtonText="Save"
                />
              )}
            </div>
            <div className="flex-1 md:flex-none">
              <DSButton
                type="button"
                variant="primary"
                dataTestId="send-confirmation-email"
                onClick={async () => {
                  await resendConfirmUser();
                  alert(
                    "success",
                    `A new confirmation code has been sent to ${data?.user?.email}`,
                  );
                }}
              >
                Send Email
              </DSButton>
            </div>
          </div>
        </div>
        <div className="flex w-3/4 lg:w-1/2 flex-1 flex-col items-center space-y-8">
          <div className="flex flex-row items-center space-x-2">
            <div className="h-8 w-8 bg-brand text-white font-bold rounded-full flex items-center justify-center">
              2
            </div>
            <h4>Enter confirmation code</h4>
          </div>

          <div className="w-full justify-center flex-1 flex flex-col space-y-4 p-10 rounded-xl shadow-md shadow-secondary bg-primary">
            <div className="flex flex-row items-center justify-center space-x-2">
              <Controller
                control={control}
                name="confirmationCode"
                rules={{ required: true }}
                render={({ field }) => (
                  <DSInput
                    onKeyDown={(e) => {
                      if (e.keyCode === 13) {
                        e.preventDefault();
                      }
                    }}
                    placeholder={"Type or paste your code here"}
                    dataTestId="confirmation-code"
                    {...field}
                  />
                )}
              />
              {errors.confirmationCode && (
                <p className="text-danger text-xs text-center">
                  Please enter your confirmation code.
                </p>
              )}
            </div>
            <div className="flex-1 md:flex-none">
              <DSButton
                type="submit"
                variant="primary"
                dataTestId="confirm-code-submit"
              >
                Confirm
              </DSButton>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
